import React, { useContext } from "react"
import _ from "lodash"

import { useFilter } from "./Filter"
import {
    countries_filter,
    reference_filter,
    free_text_filter,
} from "./utils"
import { FilterConfig } from "./utils"
import { useExternal } from "../external"
import { CompetitorFamily, CompetitorMember, CompetitorMemberStatus } from "../external/external"
import { useComments } from "../comments/CommentsProvider"
import { competitor_family, competitor_member } from "../data"
// External uses different status model (relevant/not-relevant/inactive) - no translation needed from patent states

interface FilteredExternalData {
    families: CompetitorFamily[]
    members: CompetitorMember[]
}

const FilteredExternalContext = React.createContext({
    families: [] as CompetitorFamily[],
    members: [] as CompetitorMember[],
    familyById: {} as Record<number, CompetitorFamily>,
    membersByFamilyId: {} as Record<number, CompetitorMember[]>,
    memberById: {} as Record<number, CompetitorMember>,
    memberByReference: {} as Record<string, CompetitorMember>,
})

// For competitors, "active" means relevant to us (patents + applications).
const relevantStatuses: CompetitorMemberStatus[] = ['relevant-patent', 'relevant-application']

export function FilteredExternalProvider({ children }: { children: React.ReactNode }) {
    const { activeFilters, showActiveOnly } = useFilter()
    const { families, members, membersByFamilyId: allMembersByFamilyId } = useExternal()
    const { commentsLookUp } = useComments()

    const fullContext = activeFilters
        .filter(({ isActive }) => isActive)
        .reduce(
            (ctxt: FilteredExternalData, filter: FilterConfig) => {
                switch (filter.name) {
                    case countries_filter:
                        return filterExternalByCountry(ctxt, filter.config)
                    case reference_filter:
                        return filterExternalByReference(ctxt, filter.config)
                    case free_text_filter:
                        return filterExternalByText(ctxt, filter.config, commentsLookUp)
                    default:
                        return ctxt
                }
            },
            { families, members }
        )

    // "Active only" (the default) shows only relevant competitor members; otherwise show everything
    const context = showActiveOnly === true
        ? filterByExternalStatus(fullContext, relevantStatuses, allMembersByFamilyId)
        : fullContext

    const value = {
        ...context,
        familyById: _.keyBy(context.families, f => f.competitorFamilyId),
        membersByFamilyId: _.groupBy(context.members, m => m.competitorFamilyId),
        memberById: _.keyBy(context.members, m => m.competitorMemberId),
        memberByReference: _.keyBy(context.members, m => m.internalReference),
    }

    return (
        <FilteredExternalContext.Provider value={value}>{children}</FilteredExternalContext.Provider>
    )
}

export function useFilteredExternal() {
    return useContext(FilteredExternalContext)
}

// Filter functions

function filterExternalByCountry(ctxt: FilteredExternalData, countries: string[]): FilteredExternalData {
    if (!countries || countries.length === 0) return ctxt
    const countrySet = new Set(countries)
    const filteredMembers = ctxt.members.filter(m => countrySet.has(m.countryCode))
    const familyIds = new Set(filteredMembers.map(m => m.competitorFamilyId))
    return {
        families: ctxt.families.filter(f => familyIds.has(f.competitorFamilyId)),
        members: filteredMembers,
    }
}

function filterExternalByReference(ctxt: FilteredExternalData, reference: string): FilteredExternalData {
    if (!reference || reference.trim() === '') return ctxt
    const ref = reference.toLowerCase()
    const filteredFamilies = ctxt.families.filter(f =>
        f.internalReference?.toLowerCase().includes(ref) ||
        f.externalReference?.toLowerCase().includes(ref) ||
        f.familyName?.toLowerCase().includes(ref)
    )
    const familyIds = new Set(filteredFamilies.map(f => f.competitorFamilyId))
    const filteredMembers = ctxt.members.filter(m =>
        familyIds.has(m.competitorFamilyId) ||
        m.internalReference?.toLowerCase().includes(ref) ||
        m.externalReference?.toLowerCase().includes(ref)
    )
    const memberFamilyIds = new Set(filteredMembers.map(m => m.competitorFamilyId))
    return {
        families: ctxt.families.filter(f => familyIds.has(f.competitorFamilyId) || memberFamilyIds.has(f.competitorFamilyId)),
        members: filteredMembers,
    }
}

function filterExternalByText(ctxt: FilteredExternalData, text: string, commentsLookUp: Record<string, Record<number, any[]>>): FilteredExternalData {
    if (!text || text.trim() === '') return ctxt
    const txt = text.toLowerCase()

    const matchingFamilyIds = new Set(
        ctxt.families
            .filter(f =>
                f.familyName?.toLowerCase().includes(txt) ||
                f.summary?.toLowerCase().includes(txt) ||
                f.internalReference?.toLowerCase().includes(txt) ||
                (commentsLookUp[competitor_family]?.[f.competitorFamilyId] ?? []).some(c => c.comment?.toLowerCase().includes(txt))
            )
            .map(f => f.competitorFamilyId)
    )

    const matchingMemberFamilyIds = new Set(
        ctxt.members
            .filter(m =>
                m.title?.toLowerCase().includes(txt) ||
                m.internalReference?.toLowerCase().includes(txt) ||
                m.applicationNumber?.toLowerCase().includes(txt) ||
                m.publicationNumber?.toLowerCase().includes(txt) ||
                m.patentNumber?.toLowerCase().includes(txt) ||
                (commentsLookUp[competitor_member]?.[m.competitorMemberId] ?? []).some(c => c.comment?.toLowerCase().includes(txt))
            )
            .map(m => m.competitorFamilyId)
    )

    const allFamilyIds = new Set([...matchingFamilyIds, ...matchingMemberFamilyIds])

    return {
        families: ctxt.families.filter(f => allFamilyIds.has(f.competitorFamilyId)),
        members: ctxt.members.filter(m => allFamilyIds.has(m.competitorFamilyId)),
    }
}

function filterByExternalStatus(
    ctxt: FilteredExternalData,
    statuses: CompetitorMemberStatus[],
    allMembersByFamilyId: Record<number, CompetitorMember[]>
): FilteredExternalData {
    const statusSet = new Set(statuses)
    const filteredMembers = ctxt.members.filter(m => statusSet.has(m.competitorMemberStatus))
    // Keep families that have at least one member matching the status in the full (unfiltered) member set
    const familiesWithMatchingMembers = new Set(
        ctxt.families
            .filter(f => (allMembersByFamilyId[f.competitorFamilyId] ?? []).some(m => statusSet.has(m.competitorMemberStatus)))
            .map(f => f.competitorFamilyId)
    )
    return {
        families: ctxt.families.filter(f => familiesWithMatchingMembers.has(f.competitorFamilyId)),
        members: filteredMembers,
    }
}
